import { cancelButtonProps } from "components/Form/components/CancelButton/CancelButton";
import { showConfirmModal } from "components/Modal/showConfirmModal";
import "./installPreview.scss";

/**
 * One record in an install preview (backend `POST
 * /bundle_repository/{id}/install/{key}?dry_run=true`).
 */
export interface InstallPreviewRow {
    name: string;
    record_type: string;
    id?: string;
}

export interface InstallPreview {
    dry_run?: boolean;
    bundle_key?: string;
    would_add?: InstallPreviewRow[];
    would_update?: InstallPreviewRow[];
    would_prune?: InstallPreviewRow[];
    would_overwrite?: InstallPreviewRow[];
}

// Long lists are cut so the dialog stays readable; the count says how many.
export const PREVIEW_LIST_LIMIT = 20;

/** True when the install would delete or overwrite records outside a plain update. */
export const needsInstallConfirmation = (preview?: InstallPreview | null): boolean =>
    Boolean(preview?.would_prune?.length || preview?.would_overwrite?.length);

const recordLabel = (row: InstallPreviewRow) => `${row.name} (${row.record_type.replace(/_/g, " ")})`;

const RecordList = ({ rows }: { rows: InstallPreviewRow[] }) => {
    const shown = rows.slice(0, PREVIEW_LIST_LIMIT);
    const hidden = rows.length - shown.length;
    return (
        <ul className="installPreviewList">
            {shown.map((row) => (
                <li key={`${row.record_type}/${row.name}`}>{recordLabel(row)}</li>
            ))}
            {hidden > 0 && <li>…and {hidden} more</li>}
        </ul>
    );
};

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? "" : "s"}`;

export const InstallPreviewContent = ({ preview }: { preview: InstallPreview }) => {
    const prune = preview.would_prune || [];
    const overwrite = preview.would_overwrite || [];
    const add = preview.would_add?.length || 0;
    const update = preview.would_update?.length || 0;
    return (
        <div className="installPreview">
            <p>
                The new version adds {plural(add, "record")} and updates {plural(update, "record")}.
            </p>
            {prune.length > 0 && (
                <>
                    <p>
                        <strong>
                            {plural(prune.length, "record")} will be deleted permanently
                        </strong>{" "}
                        because the new version no longer contains them:
                    </p>
                    <RecordList rows={prune} />
                </>
            )}
            {overwrite.length > 0 && (
                <>
                    <p>
                        <strong>{plural(overwrite.length, "record")} outside this bundle</strong>{" "}
                        have the same name and will be overwritten:
                    </p>
                    <RecordList rows={overwrite} />
                </>
            )}
            <p>This cannot be undone.</p>
        </div>
    );
};

/**
 * Ask the user to confirm an install whose preview deletes or overwrites
 * records. Resolves true on confirm, false on cancel; resolves true at once
 * when there is nothing to confirm.
 */
export const confirmInstallPreview = (
    preview: InstallPreview | null | undefined,
    bundleName: string,
    actionLabel = "Update"
): Promise<boolean> => {
    if (!preview || !needsInstallConfirmation(preview)) {
        return Promise.resolve(true);
    }
    return new Promise((resolve) => {
        showConfirmModal({
            title: `${actionLabel} "${bundleName}"?`,
            content: <InstallPreviewContent preview={preview} />,
            confirmText: `${actionLabel} anyway`,
            cancelText: "Cancel",
            confirmVariation: "danger",
            cancelButtonProps,
            className: "confirmationModal installPreviewModal",
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });
};
